import Breadcrumb from "components/Common/Breadcrumb"
import { useMemo, useState } from "react"
import { FaEdit, FaPlus, FaSave } from "react-icons/fa"
import { MdCancel, MdDelete } from "react-icons/md"
import {
  Badge,
  Button,
  Card,
  CardBody,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Table,
} from "reactstrap"
import React from "react"
import { usePermissionData } from "AppContext"
import Swal from "sweetalert2"
import moment from "moment"
import { Switch } from "antd"
import Select from "react-select"

// Design-first dummy employees (TODO: replace with API "users" later)
const DUMMY_EMPLOYEES = [
  { id: 1, name: "Anil Sharma" },
  { id: 2, name: "Priya Patel" },
  { id: 3, name: "Rahul Verma" },
  { id: 4, name: "Sneha Iyer" },
  { id: 5, name: "Amit Kumar" },
  { id: 6, name: "Neha Singh" },
]

// Design-first dummy data (TODO: replace with API "departments" later)
const DUMMY_DEPARTMENTS = [
  {
    id: 1,
    departmentName: "Human Resources",
    employees: [1, 2],
    isActive: true,
    createdAt: new Date(),
  },
  {
    id: 2,
    departmentName: "Sales",
    employees: [3, 4, 5],
    isActive: true,
    createdAt: new Date(),
  },
  {
    id: 3,
    departmentName: "Accounts",
    employees: [6],
    isActive: false,
    createdAt: new Date(),
  },
]

const EMPTY_FORM = { departmentName: "", employees: [] }

// Solid backgrounds for the employees dropdown (app CSS leaves it transparent)
const EMPLOYEE_SELECT_STYLES = {
  control: provided => ({
    ...provided,
    backgroundColor: "#ffffff",
    borderRadius: 16,
    minHeight: 38,
  }),
  menu: provided => ({
    ...provided,
    backgroundColor: "#ffffff",
  }),
  menuList: provided => ({
    ...provided,
    backgroundColor: "#ffffff",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "#cb9019"
      : state.isFocused
        ? "#faf3e2"
        : "#ffffff",
    color: state.isSelected ? "#ffffff" : "#263a5b",
  }),
  multiValue: provided => ({
    ...provided,
    backgroundColor: "#faf3e2",
  }),
  multiValueLabel: provided => ({
    ...provided,
    color: "#6b5a33",
  }),
}

const Departments = () => {
  document.title = "Departments | Vintage Infra Equipment Sol PVT LTD"
  const [data, setData] = useState(DUMMY_DEPARTMENTS)
  const [pageSize, setPageSize] = useState(20)
  const [searchString, setSearchString] = useState("")

  // Pagination
  const [currentPage, setCurrentPage] = useState(1)

  const [modalOpen, setModalOpen] = useState(false)
  const [selectedDepartment, setSelectedDepartment] = useState(EMPTY_FORM)
  const [errors, setErrors] = useState({})

  const { checkAccess } = usePermissionData()

  const employeeOptions = useMemo(
    () =>
      DUMMY_EMPLOYEES.map(emp => ({ value: emp.id, label: emp.name })),
    []
  )

  const getEmployeeName = id =>
    DUMMY_EMPLOYEES.find(emp => emp.id === id)?.name || "-"

  const filteredData = useMemo(() => {
    const search = searchString.toLowerCase()
    return data.filter(
      item =>
        item?.departmentName?.toLowerCase().includes(search) ||
        item?.employees?.some(id =>
          getEmployeeName(id).toLowerCase().includes(search)
        )
    )
  }, [data, searchString])

  const totalPages = Math.ceil(filteredData.length / pageSize) || 1
  const start = (currentPage - 1) * pageSize

  const paginatedData = useMemo(() => {
    const end = start + pageSize
    return filteredData.slice(start, end)
  }, [filteredData, currentPage, pageSize, start])

  const openAddModal = () => {
    setSelectedDepartment(EMPTY_FORM)
    setErrors({})
    setModalOpen(true)
  }

  const openEditModal = item => {
    setSelectedDepartment({
      ...item,
      employees: [...(item.employees || [])],
    })
    setErrors({})
    setModalOpen(true)
  }

  const validate = () => {
    const newErrors = {}
    if (!selectedDepartment?.departmentName?.trim()) {
      newErrors.departmentName = "Department name is required"
    }
    if (!selectedDepartment?.employees?.length) {
      newErrors.employees = "Select at least one employee"
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Design-first: local CRUD only (no API yet)
  const handleSubmit = () => {
    if (!validate()) return

    if (selectedDepartment?.id) {
      setData(prev =>
        prev.map(item =>
          item.id === selectedDepartment.id
            ? {
                ...item,
                departmentName: selectedDepartment.departmentName.trim(),
                employees: selectedDepartment.employees,
              }
            : item
        )
      )
      Swal.fire({
        title: "Department Updated Successfully.",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      })
    } else {
      const newItem = {
        id: Date.now(),
        departmentName: selectedDepartment.departmentName.trim(),
        employees: selectedDepartment.employees,
        isActive: true,
        createdAt: new Date(),
      }
      setData(prev => [newItem, ...prev])
      Swal.fire({
        title: "Department Added Successfully.",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      })
    }
    setModalOpen(false)
    setSelectedDepartment(EMPTY_FORM)
    setCurrentPage(1)
    // TODO: replace with FetchPostData / FetchPutData "departments" when API is ready
  }

  const handleToggleActive = item => {
    setData(prev =>
      prev.map(d =>
        d.id === item.id ? { ...d, isActive: !d.isActive } : d
      )
    )
    // TODO: replace with FetchPutData "departments" when API is ready
  }

  const handleDelete = item => {
    Swal.fire({
      title: `Are you sure you want to delete "${item?.departmentName}"?`,
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(result => {
      if (result.isConfirmed) {
        setData(prev => prev.filter(d => d.id !== item.id))
        Swal.fire({
          title: "Department Deleted Successfully.",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        })
        // TODO: replace with FetchDeleteData "departments" when API is ready
      }
    })
  }

  const renderEmployees = ids => {
    if (!ids?.length) return <span className="text-muted">-</span>
    return (
      <div className="d-flex flex-wrap gap-1">
        {ids.map(id => (
          <Badge key={id} pill color="primary" className="fw-normal">
            {getEmployeeName(id)}
          </Badge>
        ))}
      </div>
    )
  }

  return (
    <div className="page-content">
      <Breadcrumb title="Dashboard" breadcrumbItem="Departments" />

      <Card className="shadow-lg">
        <CardBody>
          {/* Top controls */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="d-flex gap-3">
              <Input
                type="select"
                value={pageSize}
                onChange={e => {
                  setPageSize(Number(e.target.value)), setCurrentPage(1)
                }}
                className="rounded-4 w-auto pe-5"
              >
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </Input>

              <Input
                className="rounded-4"
                placeholder="Search Departments"
                value={searchString}
                onChange={e => {
                  setSearchString(e.target.value)
                  setCurrentPage(1)
                }}
              />
            </div>
            {checkAccess(10, "view") && (
              <Button className="btn-blue rounded-4 px-3" onClick={openAddModal}>
                <FaPlus className="me-2" />
                Department
              </Button>
            )}
          </div>

          {/* Table */}
          <Table bordered responsive>
            <thead className="table-dark">
              <tr>
                <th style={{ width: "60px" }}>#</th>
                <th>Department Name</th>
                <th>Employees</th>
                <th style={{ width: "120px" }}>Status</th>
                <th>Created At</th>
                <th className="text-center" style={{ width: "140px" }}>
                  Action
                </th>
              </tr>
            </thead>

            <tbody>
              {paginatedData.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center">
                    No Departments found
                  </td>
                </tr>
              ) : (
                paginatedData.map((item, index) => (
                  <tr key={item.id}>
                    <td>{start + index + 1}</td>

                    <td>{item.departmentName}</td>
                    <td>{renderEmployees(item.employees)}</td>
                    <td>
                      <Switch
                        checked={item.isActive}
                        onChange={() => handleToggleActive(item)}
                      />
                    </td>
                    <td>
                      {moment(item.createdAt).format("DD-MM-YYYY | hh:mm A")}
                    </td>

                    <td className="text-center" style={{ whiteSpace: "nowrap" }}>
                      <Button
                        className="bg-success text-white me-2 border-0 shadow-none"
                        onClick={() => openEditModal(item)}
                      >
                        <FaEdit size={16} />
                      </Button>

                      <Button
                        className="bg-danger text-white border-0 shadow-none"
                        onClick={() => handleDelete(item)}
                      >
                        <MdDelete size={18} />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
          <nav
            aria-label="Page navigation example"
            className="d-flex justify-content-between mt-3"
          >
            <h5>
              Showing {filteredData.length === 0 ? 0 : start + 1} to{" "}
              {Math.min(start + pageSize, filteredData.length)} of{" "}
              {filteredData.length} entries
            </h5>
            <ul className="pagination mb-0">
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                >
                  Previous
                </button>
              </li>

              {[...Array(totalPages)].map((_, i) => {
                const pageNum = i + 1
                return (
                  <li
                    key={pageNum}
                    className={`page-item ${
                      pageNum === currentPage ? "active" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  </li>
                )
              })}

              <li
                className={`page-item ${
                  currentPage === totalPages ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() =>
                    setCurrentPage(prev => Math.min(prev + 1, totalPages))
                  }
                >
                  Next
                </button>
              </li>
            </ul>
          </nav>
        </CardBody>
      </Card>

      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} centered>
        <ModalHeader toggle={() => setModalOpen(false)}>
          {selectedDepartment?.id ? "Edit" : "Add"} Department
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <Label>
              Department Name <span className="text-danger">*</span>
            </Label>
            <Input
              placeholder="e.g. Human Resources"
              value={selectedDepartment?.departmentName || ""}
              invalid={!!errors.departmentName}
              onChange={e =>
                setSelectedDepartment(prev => ({
                  ...prev,
                  departmentName: e.target.value,
                }))
              }
            />
            {errors.departmentName && (
              <FormFeedback className="d-block">
                {errors.departmentName}
              </FormFeedback>
            )}
          </div>

          <div>
            <Label>
              Employees <span className="text-danger">*</span>
            </Label>
            <Select
              isMulti
              options={employeeOptions}
              placeholder="Select employees"
              styles={EMPLOYEE_SELECT_STYLES}
              value={employeeOptions.filter(opt =>
                selectedDepartment?.employees?.includes(opt.value)
              )}
              onChange={selected =>
                setSelectedDepartment(prev => ({
                  ...prev,
                  employees: (selected || []).map(opt => opt.value),
                }))
              }
              className={errors.employees ? "is-invalid" : ""}
            />
            {errors.employees && (
              <div className="invalid-feedback d-block">
                {errors.employees}
              </div>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button className="btn-blue" onClick={handleSubmit}>
            <FaSave /> Save
          </Button>
          <Button className="btn-danger" onClick={() => setModalOpen(false)}>
            <MdCancel /> Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default Departments
